import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Calendar, Clock, ArrowLeft, Play, Ticket, Share2, CheckCircle2, Tag, X, Loader2, Film, Bell, BellOff, LogIn } from 'lucide-react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import usePageTracking from '../hooks/usePageTracking';
import SocialShareModal from '../components/SocialShareModal';
import PromoVideoPlayer from '../components/PromoVideoPlayer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Countdown Timer Component
const CountdownTimer = ({ eventDate, onComplete, compact = false }) => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const eventTime = new Date(eventDate).getTime();
      const now = new Date().getTime();
      const difference = eventTime - now;

      if (difference <= 0) {
        setIsComplete(true);
        if (onComplete) onComplete();
        return { days: 0, hours: 0, minutes: 0, seconds: 0 };
      }

      return {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((difference % (1000 * 60)) / 1000)
      };
    };

    setTimeLeft(calculateTimeLeft());
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, [eventDate, onComplete]);

  if (isComplete) {
    return (
      <div className={`bg-gradient-to-r from-red-600 to-pink-600 rounded-xl ${compact ? 'p-3' : 'p-6'} text-center`}>
        <p className={`text-white font-bold ${compact ? 'text-sm' : 'text-xl'} animate-pulse`}>EVENT IS LIVE!</p>
      </div>
    );
  }

  const TimeBlock = ({ value, label }) => (
    <div className="flex flex-col items-center">
      <div className={`bg-black/50 backdrop-blur-sm rounded-lg ${compact ? 'px-2 py-1.5 min-w-[40px]' : 'px-4 py-3 min-w-[70px]'} border border-white/10`}>
        <span className={`${compact ? 'text-lg' : 'text-3xl sm:text-4xl'} font-bold text-white tabular-nums`}>
          {String(value).padStart(2, '0')}
        </span>
      </div>
      <span className={`${compact ? 'text-[10px]' : 'text-xs sm:text-sm'} text-gray-400 mt-1 uppercase tracking-wider`}>{label}</span>
    </div>
  );

  if (compact) {
    return (
      <div className="flex items-center justify-center gap-2">
        <TimeBlock value={timeLeft.days} label="Days" />
        <span className="text-lg text-gray-500 font-bold">:</span>
        <TimeBlock value={timeLeft.hours} label="Hrs" />
        <span className="text-lg text-gray-500 font-bold">:</span>
        <TimeBlock value={timeLeft.minutes} label="Min" />
        <span className="text-lg text-gray-500 font-bold">:</span>
        <TimeBlock value={timeLeft.seconds} label="Sec" />
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-purple-900/30 to-pink-900/30 rounded-2xl p-6 border border-purple-500/20">
      <p className="text-center text-gray-400 mb-4 text-sm uppercase tracking-wider">Event starts in</p>
      <div className="flex items-center justify-center gap-3 sm:gap-4">
        <TimeBlock value={timeLeft.days} label="Days" />
        <span className="text-2xl text-gray-500 font-bold">:</span>
        <TimeBlock value={timeLeft.hours} label="Hours" />
        <span className="text-2xl text-gray-500 font-bold">:</span>
        <TimeBlock value={timeLeft.minutes} label="Mins" />
        <span className="text-2xl text-gray-500 font-bold hidden sm:block">:</span>
        <div className="hidden sm:block">
          <TimeBlock value={timeLeft.seconds} label="Secs" />
        </div>
      </div>
    </div>
  );
};

const LiveEventDetail = () => {
  const { slug } = useParams();
  usePageTracking(`event-${slug}`);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showTrailerModal, setShowTrailerModal] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Promo code state
  const [promoCode, setPromoCode] = useState('');
  const [promoInput, setPromoInput] = useState('');
  const [promoValidating, setPromoValidating] = useState(false);
  const [promoError, setPromoError] = useState('');
  const [promoDiscount, setPromoDiscount] = useState(null);
  const [showPromoInput, setShowPromoInput] = useState(false);
  
  // Reminder state
  const [reminderSet, setReminderSet] = useState(false);
  const [settingReminder, setSettingReminder] = useState(false);
  const [pushSupported, setPushSupported] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);

  useEffect(() => {
    fetchEvent();
    
    // Check login status
    const token = localStorage.getItem('livestreamToken');
    const customerData = localStorage.getItem('livestreamCustomer');
    setIsLoggedIn(!!(token && customerData));
    
    // Check if user is admin
    const adminToken = localStorage.getItem('adminToken');
    setIsAdmin(!!adminToken);
    
    // Check for promo code in URL
    const urlPromo = searchParams.get('promo');
    if (urlPromo) {
      setPromoInput(urlPromo);
      setShowPromoInput(true);
    }
    
    // Check for payment success - redirect to thank you page
    const sessionId = searchParams.get('session_id');
    const eventId = searchParams.get('event_id');
    if (sessionId && eventId) {
      navigate(`/live/payment-success?session_id=${sessionId}&event_id=${eventId}`);
      return;
    }
    
    // Check push notification support
    if ('Notification' in window && 'serviceWorker' in navigator) {
      setPushSupported(true);
    }
  }, [slug, searchParams, navigate]);

  // Auto-checkout after returning from login
  useEffect(() => {
    const autoCheckout = searchParams.get('auto_checkout');
    if (autoCheckout === 'true' && event && !purchasing) {
      const token = localStorage.getItem('livestreamToken');
      const customer = JSON.parse(localStorage.getItem('livestreamCustomer') || '{}');
      
      if (token && customer.id) {
        // Clear the auto_checkout flag from URL and proceed with checkout
        const newUrl = window.location.pathname + (promoCode ? `?promo=${promoCode}` : '');
        window.history.replaceState({}, '', newUrl);
        
        // Trigger checkout
        handlePurchase();
      }
    }
  }, [event, searchParams]);

  // Check if reminder is already set for this event
  useEffect(() => {
    if (event?.id) {
      checkReminderStatus();
    }
  }, [event?.id]);
  
  // Store restricted event info for use on other pages (My Tickets, etc.)
  useEffect(() => {
    if (event?.restricted_navigation) {
      localStorage.setItem('restrictedEvent', JSON.stringify({
        slug: event.slug,
        title: event.title,
        custom_logo_url: event.custom_logo_url,
        custom_signin_wallpaper_url: event.custom_signin_wallpaper_url
      }));
    }
  }, [event]);

  // Auto-validate promo from URL
  useEffect(() => {
    const urlPromo = searchParams.get('promo');
    if (urlPromo && event && !promoDiscount) {
      validatePromoCode(urlPromo);
    }
  }, [event, searchParams]);

  const fetchEvent = async () => {
    try {
      const response = await axios.get(`${API}/livestream/events/${slug}`);
      setEvent(response.data);
    } catch (err) {
      console.error('Error fetching event:', err);
    } finally {
      setLoading(false);
    }
  };

  const checkReminderStatus = async () => {
    const token = localStorage.getItem('livestreamToken');
    if (!token || !event?.id) return;
    
    try {
      const response = await axios.get(
        `${API}/livestream/reminders/${event.id}/status`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReminderSet(response.data.reminder_set);
    } catch (err) {
      console.error('Error checking reminder status:', err);
    }
  };

  const toggleReminder = async () => {
    const token = localStorage.getItem('livestreamToken');
    if (!token) {
      localStorage.setItem('intendedEvent', slug);
      navigate('/live/login');
      return;
    }

    setSettingReminder(true);
    try {
      if (reminderSet) {
        // Cancel reminder
        await axios.delete(
          `${API}/livestream/reminders/${event.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setReminderSet(false);
      } else {
        // Set reminder - request notification permission if needed
        if (pushSupported && Notification.permission === 'default') {
          const permission = await Notification.requestPermission();
          if (permission !== 'granted') {
            console.log('Push notifications not granted, will use email only');
          }
        }
        
        await axios.post(
          `${API}/livestream/reminders/${event.id}`,
          { 
            email_reminder: true,
            push_reminder: Notification.permission === 'granted'
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setReminderSet(true);
      }
    } catch (err) {
      console.error('Error toggling reminder:', err);
      alert(err.response?.data?.detail || 'Failed to set reminder');
    } finally {
      setSettingReminder(false);
    }
  };

  const validatePromoCode = async (code) => {
    if (!code.trim() || !event) return;
    
    setPromoValidating(true);
    setPromoError('');
    
    try {
      const response = await axios.post(`${API}/livestream/validate-promo`, {
        code: code.trim(),
        event_id: event.id
      });
      
      setPromoDiscount(response.data);
      setPromoCode(code.trim().toUpperCase());
      setPromoError('');
    } catch (err) {
      setPromoError(err.response?.data?.detail || 'Invalid promo code');
      setPromoDiscount(null);
      setPromoCode('');
    } finally {
      setPromoValidating(false);
    }
  };

  const removePromoCode = () => {
    setPromoCode('');
    setPromoDiscount(null);
    setPromoInput('');
    setPromoError('');
  };

  const handleSignIn = () => {
    // Store the event for branding on login page
    localStorage.setItem('intendedEvent', slug);
    if (event?.restricted_navigation) {
      localStorage.setItem('restrictedEvent', JSON.stringify({
        slug: event.slug,
        title: event.title,
        custom_logo_url: event.custom_logo_url,
        custom_signin_wallpaper_url: event.custom_signin_wallpaper_url
      }));
    }
    // Redirect to event page - the login handler will check if user has ticket
    // and redirect to My Tickets if they do, or back to event page if they don't
    navigate(`/live/login?redirect=/live/event/${slug}`);
  };

  const handlePurchase = async () => {
    const token = localStorage.getItem('livestreamToken');
    const customer = JSON.parse(localStorage.getItem('livestreamCustomer') || '{}');
    
    if (!token || !customer.id) {
      // Save intended event and promo code for after login
      localStorage.setItem('intendedEvent', slug);
      localStorage.setItem('intendedEventAction', 'checkout'); // Flag to auto-checkout after login
      if (promoCode) {
        localStorage.setItem('intendedPromo', promoCode);
      }
      // Use redirect parameter for both regular and Google login
      navigate(`/live/login?redirect=/live/event/${slug}?auto_checkout=true${promoCode ? `&promo=${promoCode}` : ''}`);
      return;
    }

    setPurchasing(true);
    try {
      const response = await axios.post(`${API}/livestream/payment/create-checkout`, {
        event_id: event.id,
        customer_id: customer.id,
        origin_url: window.location.origin,
        promo_code: promoCode || null
      });

      // Redirect to checkout (or success page if free)
      window.location.href = response.data.checkout_url;
    } catch (err) {
      console.error('Error creating checkout:', err);
      alert(err.response?.data?.detail || 'Failed to create checkout session');
    } finally {
      setPurchasing(false);
    }
  };

  const handleShare = () => {
    setShowShareModal(true);
  };

  // Production domain for share links
  const PRODUCTION_DOMAIN = 'https://ltvproduction.com';

  const getShareUrl = () => {
    // Always use production domain for share URLs, not window.location
    let url = `${PRODUCTION_DOMAIN}/event/${event.slug}`;
    if (promoCode) {
      url += `?promo=${promoCode}`;
    }
    return url;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getCurrencySymbol = () => event?.display_symbol || (event?.currency === 'GBP' ? '£' : '$');

  const getDisplayPrice = () => {
    if (promoDiscount) {
      return promoDiscount.final_price;
    }
    return event?.display_price ?? event?.price ?? 0;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-2 border-white border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center text-white">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Event Not Found</h1>
          <Button onClick={() => navigate('/live')}>Back to Events</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white pb-24 lg:pb-0">
      {/* Dynamic Meta Tags for Social Sharing */}
      {event && (
        <Helmet>
          <title>{event.title} | LTV Live</title>
          <meta name="description" content={event.description} />
          
          {/* Open Graph / Facebook */}
          <meta property="og:type" content="website" />
          <meta property="og:url" content={getShareUrl()} />
          <meta property="og:title" content={`${event.title} | LTV Live`} />
          <meta property="og:description" content={event.description} />
          <meta property="og:image" content={event.social_share_image || event.thumbnail_url || `${window.location.origin}/ltv-live-og.png`} />
          <meta property="og:image:width" content="1200" />
          <meta property="og:image:height" content="630" />
          <meta property="og:site_name" content="LTV Live" />
          
          {/* Twitter */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:url" content={getShareUrl()} />
          <meta name="twitter:title" content={`${event.title} | LTV Live`} />
          <meta name="twitter:description" content={event.description} />
          <meta name="twitter:image" content={event.social_share_image || event.thumbnail_url || `${window.location.origin}/ltv-live-og.png`} />
        </Helmet>
      )}

      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#0a0a0a]/95 backdrop-blur-md border-b border-[#222222]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 grid grid-cols-3 items-center">
          {/* Left - Back Button (hidden if restricted navigation, but visible for admins) */}
          <div className="flex justify-start">
            {(!event.restricted_navigation || isAdmin) && (
              <Button 
                variant="ghost" 
                size="sm"
                className={`text-white hover:bg-white/10 ${isAdmin && event.restricted_navigation ? 'border border-amber-500/50' : ''}`}
                onClick={() => navigate('/live')}
                title={isAdmin && event.restricted_navigation ? 'Admin: Back to Events' : 'Back to Events'}
              >
                <ArrowLeft className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">
                  {isAdmin && event.restricted_navigation ? 'Admin: Back' : 'Back to Events'}
                </span>
              </Button>
            )}
          </div>
          
          {/* Center - Custom Logo or LTV Live Logo */}
          <div className="flex justify-center items-center">
            <button 
              onClick={() => (!event.restricted_navigation || isAdmin) && navigate('/live')}
              className={`flex items-center gap-2 transition-opacity ${(event.restricted_navigation && !isAdmin) ? 'cursor-default' : 'hover:opacity-80'}`}
              data-testid="header-logo"
            >
              {event.custom_logo_url ? (
                <img 
                  src={event.custom_logo_url} 
                  alt={event.title} 
                  className="h-8 sm:h-10 max-w-[150px] sm:max-w-[200px] object-contain"
                />
              ) : (
                <>
                  <img 
                    src="/ltv-live-logo.png" 
                    alt="LTV Live" 
                    className="h-8 w-8 sm:h-10 sm:w-10"
                  />
                  <span className="text-white font-semibold text-lg sm:text-xl tracking-tight">LTV Live</span>
                </>
              )}
            </button>
          </div>
          
          {/* Right - Sign In (if not logged in) or My Tickets + Share Button */}
          <div className="flex justify-end gap-2">
            {!isLoggedIn && (
              <Button 
                size="sm"
                className="bg-red-600 hover:bg-red-500 text-white"
                onClick={handleSignIn}
                data-testid="sign-in-btn"
              >
                <LogIn className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Sign In</span>
              </Button>
            )}
            {isLoggedIn && (
              <Button 
                size="sm"
                variant="outline"
                className="border-red-600 text-red-500 hover:bg-red-600 hover:text-white"
                onClick={() => navigate('/live/my-tickets')}
                data-testid="my-tickets-btn"
              >
                <Ticket className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">My Tickets</span>
              </Button>
            )}
            <Button 
              variant="ghost"
              size="sm"
              className="text-white hover:bg-white/10"
              onClick={handleShare}
            >
              <Share2 className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Share Event</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Mobile Sticky Bottom CTA Bar */}
      <div className="fixed bottom-0 left-0 right-0 z-50 lg:hidden bg-[#111111] border-t border-[#333333] px-4 py-3 safe-area-pb">
        {event.status === 'ended' ? (
          <div className="flex items-center justify-between gap-4">
            <div className="flex-shrink-0">
              <div className="text-lg font-bold text-gray-400">Event Over</div>
            </div>
            {event.vod_video ? (
              <Button
                className="flex-1 bg-red-500 hover:bg-red-600 text-white text-base py-5"
                onClick={() => navigate(`/vod/video/${event.vod_video.slug}`)}
                data-testid="mobile-watch-vod-btn"
              >
                <Film className="h-5 w-5 mr-2" />
                Watch back on VOD
              </Button>
            ) : (
              <Button
                className="flex-1 bg-gray-700 text-gray-300 text-base py-5"
                onClick={() => navigate('/live')}
              >
                Browse Events
              </Button>
            )}
          </div>
        ) : (
        <div className="flex items-center justify-between gap-4">
          <div className="flex-shrink-0">
            {promoDiscount ? (
              <div>
                <span className="text-gray-500 line-through text-sm">{getCurrencySymbol()}{event.price.toFixed(2)}</span>
                <div className="text-2xl font-bold text-green-400">
                  {promoDiscount.is_free ? 'FREE' : `${getCurrencySymbol()}${promoDiscount.final_price.toFixed(2)}`}
                </div>
              </div>
            ) : (
              <div>
                <div className="text-2xl font-bold text-white">{getCurrencySymbol()}{event.price.toFixed(2)}</div>
                <span className="text-xs text-gray-400">per viewer</span>
              </div>
            )}
          </div>
          <Button 
            className={`flex-1 text-white text-base py-5 ${
              promoDiscount?.is_free 
                ? 'bg-green-600 hover:bg-green-700' 
                : 'bg-red-500 hover:bg-red-600'
            }`}
            onClick={handlePurchase}
            disabled={purchasing}
            data-testid="mobile-buy-ticket-btn"
          >
            {purchasing ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Processing...
              </>
            ) : promoDiscount?.is_free ? (
              <>
                <Ticket className="h-5 w-5 mr-2" />
                Get Free Ticket
              </>
            ) : (
              <>
                <Ticket className="h-5 w-5 mr-2" />
                Buy Ticket Now
              </>
            )}
          </Button>
        </div>
        )}
      </div>

      {/* Event Details */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Thumbnail */}
            <div className="relative h-48 sm:h-64 md:h-96 bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl sm:rounded-2xl overflow-hidden mb-4 sm:mb-8">
              {event.thumbnail_url ? (
                <>
                  {/* Mobile thumbnail (shown on small screens if available) */}
                  <img 
                    src={event.mobile_thumbnail_url || event.thumbnail_url} 
                    alt={event.title}
                    className="w-full h-full object-cover sm:hidden"
                  />
                  {/* Desktop thumbnail (shown on larger screens) */}
                  <img 
                    src={event.thumbnail_url} 
                    alt={event.title}
                    className="w-full h-full object-cover hidden sm:block"
                  />
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Play className="h-16 w-16 sm:h-24 sm:w-24 text-gray-700" />
                </div>
              )}
              {event.status === 'live' && (
                <div className="absolute top-3 left-3 sm:top-4 sm:left-4">
                  <Badge className="bg-red-500 text-white animate-pulse text-sm sm:text-lg px-3 py-1 sm:px-4 sm:py-2">
                    LIVE NOW
                  </Badge>
                </div>
              )}
              {event.status === 'ended' && (
                <div className="absolute top-3 left-3 sm:top-4 sm:left-4">
                  <Badge className="bg-gray-600 text-white text-sm sm:text-lg px-3 py-1 sm:px-4 sm:py-2" data-testid="event-ended-badge">
                    EVENT OVER
                  </Badge>
                </div>
              )}
              {/* Mobile Price Overlay */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent p-4 lg:hidden">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-white font-bold text-lg line-clamp-1">{event.title}</h2>
                    <p className="text-gray-300 text-sm">{formatDate(event.event_date)} • {formatTime(event.event_date)}</p>
                  </div>
                  {promoDiscount ? (
                    <div className="text-right">
                      <span className="text-gray-400 line-through text-sm">{getCurrencySymbol()}{event.price.toFixed(2)}</span>
                      <Badge className="ml-2 bg-green-600">{promoDiscount.discount_type === 'percentage' ? `${promoDiscount.discount_value}%` : `${getCurrencySymbol()}${promoDiscount.discount_amount}`} OFF</Badge>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>

            {/* Title & Description */}
            <div className="mb-6 sm:mb-8">
              <div className="flex flex-wrap gap-2 mb-3 sm:mb-4">
                {event.status === 'upcoming' && (
                  <Badge className="bg-blue-600">Upcoming</Badge>
                )}
                {event.voting_enabled && (
                  <Badge className="bg-purple-600">Live Voting</Badge>
                )}
              </div>
              <h1 className="text-2xl sm:text-4xl md:text-5xl font-bold mb-3">{event.title}</h1>
              
              {/* Date & Time - Between Title and Trailer */}
              <div className="flex items-center gap-4 text-gray-400 mb-4">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-red-500" />
                  <span className="text-sm sm:text-base">{formatDate(event.event_date)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-red-500" />
                  <span className="text-sm sm:text-base">{formatTime(event.event_date)}</span>
                </div>
              </div>

              {/* Trailer Button - Below Date/Time */}
              {event.show_promo_video && event.promo_video_url && (
                <button
                  onClick={() => setShowTrailerModal(true)}
                  className="group flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-500 hover:to-pink-500 rounded-xl transition-all duration-300 mb-4"
                >
                  <Play className="h-4 w-4 text-white fill-white" />
                  <span className="text-white font-semibold text-sm">Watch Trailer</span>
                </button>
              )}
              {/* Description with Show More/Less */}
              {event.description && (
                <div className="text-base sm:text-xl text-gray-400 leading-relaxed">
                  {event.description.length > 200 ? (
                    <>
                      <p>
                        {showFullDescription 
                          ? event.description 
                          : `${event.description.substring(0, 200)}...`
                        }
                      </p>
                      <button
                        onClick={() => setShowFullDescription(!showFullDescription)}
                        className="text-red-500 hover:text-red-400 font-medium mt-2 text-sm sm:text-base transition-colors"
                      >
                        {showFullDescription ? 'Show less' : 'Show more'}
                      </button>
                    </>
                  ) : (
                    <p>{event.description}</p>
                  )}
                </div>
              )}
            </div>

            {/* Event Over Notice - shown on mobile for ended events */}
            {event.status === 'ended' && (
              <div className="mb-6 lg:hidden" data-testid="mobile-event-over-notice">
                <div className="bg-[#111111] border border-[#222222] rounded-xl p-6 text-center">
                  <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-gray-800 flex items-center justify-center">
                    <Clock className="h-7 w-7 text-gray-500" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-1">Event Over</h3>
                  <p className="text-gray-400 text-sm mb-4">This live event has concluded.</p>
                  {event.vod_video && (
                    <Button
                      className="w-full bg-red-500 hover:bg-red-600 text-white py-4 mb-3"
                      onClick={() => navigate(`/vod/video/${event.vod_video.slug}`)}
                      data-testid="mobile-inline-watch-vod-btn"
                    >
                      <Film className="h-5 w-5 mr-2" />
                      Watch back on VOD
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    className="w-full border-gray-700 text-gray-300 hover:bg-gray-800"
                    onClick={() => navigate('/live')}
                  >
                    Browse Upcoming Events
                  </Button>
                </div>
              </div>
            )}

            {/* Countdown Timer - Only for mobile on upcoming events */}
            {event.status === 'upcoming' && (
              <div className="mb-6 lg:hidden">
                <CountdownTimer eventDate={event.event_date} />
                
                {/* Reminder Button */}
                <div className="mt-4 flex justify-center">
                  <Button
                    variant="outline"
                    onClick={toggleReminder}
                    disabled={settingReminder}
                    className={`${
                      reminderSet 
                        ? 'border-green-500/50 text-green-400 bg-green-500/10' 
                        : 'border-purple-500/50 text-purple-400 hover:bg-purple-500/10'
                    }`}
                  >
                    {settingReminder ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : reminderSet ? (
                      <BellOff className="h-4 w-4 mr-2" />
                    ) : (
                      <Bell className="h-4 w-4 mr-2" />
                    )}
                    {reminderSet ? 'Reminder Set' : 'Remind Me'}
                  </Button>
                </div>
              </div>
            )}

            {/* Mobile Promo Code Section - Green text (hide when ended) */}
            {event.status !== 'ended' && (
            <div className="lg:hidden mb-6">
              {!promoDiscount && (
                <div className="bg-[#111111] rounded-xl p-4 border border-[#222222]">
                  {showPromoInput ? (
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <Input
                          placeholder="Enter promo code"
                          value={promoInput}
                          onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                          className="bg-[#1a1a1a] border-[#333333] text-white uppercase"
                          disabled={promoValidating}
                        />
                        <Button
                          variant="outline"
                          className="border-green-500 text-green-400 hover:bg-green-500/10"
                          onClick={() => validatePromoCode(promoInput)}
                          disabled={promoValidating || !promoInput.trim()}
                        >
                          {promoValidating ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Apply'}
                        </Button>
                      </div>
                      {promoError && <p className="text-red-400 text-sm">{promoError}</p>}
                      <button
                        onClick={() => { setShowPromoInput(false); setPromoInput(''); setPromoError(''); }}
                        className="text-gray-500 text-sm hover:text-gray-400"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowPromoInput(true)}
                      className="flex items-center gap-2 text-green-400 hover:text-green-300 text-sm w-full justify-center py-1"
                    >
                      <Tag className="h-4 w-4" />
                      Have a Promo Code?
                    </button>
                  )}
                </div>
              )}
              {promoDiscount && (
                <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                    <span className="text-green-400 font-medium">
                      {promoDiscount.discount_type === 'percentage' 
                        ? `${promoDiscount.discount_value}% discount applied!`
                        : `${getCurrencySymbol()}${promoDiscount.discount_amount} off applied!`
                      }
                    </span>
                  </div>
                  <button onClick={removePromoCode} className="text-gray-400 hover:text-white">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
            )}

            {/* What's Included - Mobile (hide when ended) */}
            {event.status !== 'ended' && (
            <div className="lg:hidden bg-[#111111] rounded-xl p-4 border border-[#222222] mb-6">
              <h3 className="font-bold text-white mb-3">What's Included</h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-gray-400">
                  <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
                  <span>Full HD streaming quality</span>
                </div>
                <div className="flex items-center gap-2 text-gray-400">
                  <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
                  <span>Watch on any device</span>
                </div>
                <div className="flex items-center gap-2 text-gray-400">
                  <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
                  <span>Secure payment via Stripe</span>
                </div>
                {event.voting_enabled && (
                  <div className="flex items-center gap-2 text-gray-400">
                    <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
                    <span>Participate in live voting</span>
                  </div>
                )}
              </div>
            </div>
            )}
          </div>

          {/* Sidebar - Purchase Card (Hidden on Mobile - using sticky bottom bar instead) */}
          <div className="hidden lg:block lg:col-span-1">
            <Card className="bg-[#111111] border-[#222222] sticky top-24">
              {event.status === 'ended' ? (
                <>
                  <CardHeader>
                    <CardTitle className="text-white">Event Over</CardTitle>
                    <CardDescription>This event has ended</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="text-center py-6">
                      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-800 flex items-center justify-center">
                        <Clock className="h-8 w-8 text-gray-500" />
                      </div>
                      <p className="text-gray-400 text-sm">Thank you for your interest. This live event has concluded.</p>
                    </div>
                    {event.vod_video && (
                      <Button
                        className="w-full bg-red-500 hover:bg-red-600 text-white text-lg py-6"
                        onClick={() => navigate(`/vod/video/${event.vod_video.slug}`)}
                        data-testid="watch-vod-btn"
                      >
                        <Film className="h-5 w-5 mr-2" />
                        Watch back on VOD
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      className="w-full border-gray-700 text-gray-300 hover:bg-gray-800"
                      onClick={() => navigate('/live')}
                    >
                      Browse Upcoming Events
                    </Button>
                  </CardContent>
                </>
              ) : (
                <>
              <CardHeader>
                <CardTitle className="text-white">Get Your Ticket</CardTitle>
                <CardDescription>One-time purchase for full access</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Countdown Timer - Inside ticket card */}
                {event.status === 'upcoming' && (
                  <div className="bg-gradient-to-br from-purple-900/20 to-pink-900/20 rounded-xl p-4 border border-purple-500/20">
                    <p className="text-center text-gray-400 mb-3 text-xs uppercase tracking-wider">Event starts in</p>
                    <CountdownTimer eventDate={event.event_date} compact={true} />
                  </div>
                )}

                {/* Price Display */}
                <div className="text-center py-4">
                  {promoDiscount ? (
                    <>
                      <div className="text-2xl text-gray-500 line-through mb-1">
                        {getCurrencySymbol()}{event.price.toFixed(2)}
                      </div>
                      <div className="text-5xl font-bold text-green-400 mb-2">
                        {promoDiscount.is_free ? 'FREE' : `${getCurrencySymbol()}${promoDiscount.final_price.toFixed(2)}`}
                      </div>
                      <div className="flex items-center justify-center gap-2">
                        <Badge className="bg-green-600 text-white">
                          <Tag className="h-3 w-3 mr-1" />
                          {promoDiscount.discount_type === 'percentage' 
                            ? `${promoDiscount.discount_value}% OFF`
                            : `${getCurrencySymbol()}${promoDiscount.discount_amount} OFF`
                          }
                        </Badge>
                        <button 
                          onClick={removePromoCode}
                          className="text-gray-400 hover:text-white"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="text-5xl font-bold text-white mb-2">
                        {getCurrencySymbol()}{event.price.toFixed(2)}
                      </div>
                      <p className="text-gray-400">per viewer</p>
                    </>
                  )}
                </div>

                {/* Promo Code Applied Badge */}
                {promoDiscount && (
                  <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Tag className="h-4 w-4 text-green-400" />
                        <span className="text-green-400 font-medium">{promoCode}</span>
                      </div>
                      <span className="text-green-400">
                        {promoDiscount.is_free ? 'FREE!' : `-${promoDiscount.discount}%`}
                      </span>
                    </div>
                  </div>
                )}

                <div className="space-y-3 text-sm">
                  <div className="flex items-center gap-2 text-gray-400">
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                    <span>Full HD streaming quality</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-400">
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                    <span>Watch on any device</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-400">
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                    <span>Secure payment via Stripe</span>
                  </div>
                  {event.voting_enabled && (
                    <div className="flex items-center gap-2 text-gray-400">
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                      <span>Participate in live voting</span>
                    </div>
                  )}
                </div>

                {/* Promo Code Section - Green text, between features and buy button */}
                {!promoDiscount && (
                  <div>
                    {showPromoInput ? (
                      <div className="space-y-2">
                        <div className="flex gap-2">
                          <Input
                            placeholder="Enter promo code"
                            value={promoInput}
                            onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                            className="bg-[#1a1a1a] border-[#333333] text-white uppercase"
                            disabled={promoValidating}
                            data-testid="promo-code-input"
                          />
                          <Button
                            variant="outline"
                            className="border-green-500 text-green-400 hover:bg-green-500/10"
                            onClick={() => validatePromoCode(promoInput)}
                            disabled={promoValidating || !promoInput.trim()}
                            data-testid="apply-promo-btn"
                          >
                            {promoValidating ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              'Apply'
                            )}
                          </Button>
                        </div>
                        {promoError && (
                          <p className="text-red-400 text-sm">{promoError}</p>
                        )}
                        <button
                          onClick={() => {
                            setShowPromoInput(false);
                            setPromoInput('');
                            setPromoError('');
                          }}
                          className="text-gray-500 text-sm hover:text-gray-400"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setShowPromoInput(true)}
                        className="flex items-center gap-2 text-green-400 hover:text-green-300 text-sm w-full justify-center"
                        data-testid="have-promo-btn"
                      >
                        <Tag className="h-4 w-4" />
                        Have a Promo Code?
                      </button>
                    )}
                  </div>
                )}

                <Button 
                  className={`w-full text-white text-lg py-6 ${
                    promoDiscount?.is_free 
                      ? 'bg-green-600 hover:bg-green-700' 
                      : 'bg-red-500 hover:bg-red-600'
                  }`}
                  onClick={handlePurchase}
                  disabled={purchasing || event.status === 'ended'}
                  data-testid="buy-ticket-btn"
                >
                  {purchasing ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Processing...
                    </>
                  ) : event.status === 'ended' ? (
                    'Event Ended'
                  ) : promoDiscount?.is_free ? (
                    <>
                      <Ticket className="h-5 w-5 mr-2" />
                      Get Free Ticket
                    </>
                  ) : (
                    <>
                      <Ticket className="h-5 w-5 mr-2" />
                      Buy Ticket Now
                    </>
                  )}
                </Button>

                <p className="text-xs text-gray-500 text-center">
                  By purchasing, you agree to our terms. Tickets are non-refundable and for single-device use only.
                </p>
              </CardContent>
                </>
              )}
            </Card>
          </div>
        </div>
      </div>

      {/* Share Modal */}
      {showShareModal && event && (
        <SocialShareModal 
          event={event}
          shareUrl={getShareUrl()}
          onClose={() => setShowShareModal(false)}
        />
      )}

      {/* Trailer Modal - Zeus Style */}
      {showTrailerModal && event?.promo_video_url && (
        <div 
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4"
          onClick={() => setShowTrailerModal(false)}
        >
          <div 
            className="relative w-full max-w-5xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => setShowTrailerModal(false)}
              className="absolute -top-12 right-0 text-white/70 hover:text-white flex items-center gap-2 transition-colors"
            >
              <span className="text-sm">Close</span>
              <X className="h-6 w-6" />
            </button>
            
            {/* Title */}
            <h3 className="text-white text-xl font-bold mb-4 flex items-center gap-2">
              <Film className="h-5 w-5 text-red-500" />
              {event.title} - Trailer
            </h3>
            
            {/* Video Player */}
            <div className="aspect-video bg-black rounded-2xl overflow-hidden shadow-2xl shadow-red-500/20">
              <PromoVideoPlayer 
                src={event.promo_video_url}
                poster={event.thumbnail_url}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LiveEventDetail;
